import { PortalTelemetryEvent } from '../../../app/telemetry/portalTelemetryEvents';
import { PortalTelemetryScreen } from '../../../app/telemetry/portalTelemetryScreens';
import { validationIssues } from '../../../app/telemetry/validationTelemetry';
import { portalAction, portalScreenContext, trackPortalEvent } from '../../../app/telemetry/portalTelemetry';
import { usePortalScreen } from '../../../app/telemetry/usePortalTelemetry';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid,
    Paper, Tab, Tabs, Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { PCSDetails } from "./PCSDetails";
import { PersonalDetails } from "./PersonalDetails";
import { LocationAndPreferences } from "./LocationAndPreferences";
import { toast } from "react-toastify";
import { getPersonalDetailsAsync, updatePersonalDetailsStateAsync, updateVerifyPersonalDetailsAsync }
    from "../../../app/slices/personSlice";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import { PersonalDetailsDto } from "../../../app/models/person/personalDetailsDto";
import {
    API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_NOT_A_VALUE, MESSAGE_PROCESSING_DETAILS
} from "../../../app/utils/constant";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup';
import { updateProfileValidationSchemas } from "../common/updateProfileValidationSchema";
import { ApiRequestStatus } from "../../../app/enums/apiRequestStatus";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { getAlumniDetails, updateAlumniDetails } from "../../../app/slices/alumniSlice";
import MemberPostalAddress from "../../../app/models/membership/MemberPostalAddress";
import { getFAMDetails, updateFAMDetails } from "../../../app/slices/famSlice";
import { useMsal } from "@azure/msal-react";
import { UserClaims } from "../../../app/enums/userClaims";
import { IdTokenClaims } from "../../../app/models/account/idTokenClaims";
import { alumniFilter, famFilter, affiliateAssociateFilter, pcsFilter, formatDate } from "../../../app/utils/util";
import { useGetFamDetails } from "../../../app/customHooks/useGetFamDetails";
import { useGetPersonalDetails } from "../../../app/customHooks/useGetPersonalDetails";
import { useReferenceData } from "../../../app/customHooks/useReferenceData";
import { useGetAlumniDetails } from "../../../app/customHooks/useGetAlumniDetails";
import { UpdateProfileStepsEnum } from "../common/updateProfileStepsEnum";
import { PCSDetailsDto } from "../../../app/models/pcsEnrolment/pcsDetailsDto";
import { saveRegistrationDetailsAsync, getRegistrationDetailsAsync } from "../../../app/slices/pcsSlice";
import { getWorkpostListAsync, saveWorkpostAsync } from "../../../app/slices/workpostSlice";
import { WorkPostDto } from "../../../app/models/workpost/workPostDto";
import WorkDetails from "../../pcsEnrolment/workDetails/WorkDetails";
import { SpaName } from "../../../app/enums/spaName";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

export default function UpdateProfile() {
    const appInsights = useAppInsightsContext();

    const [tabs, setTabs] = useState<string[]>([UpdateProfileStepsEnum.PersonalDetails]);

    const { instance, accounts } = useMsal();
    const userRoles = (instance.getActiveAccount()?.idTokenClaims as unknown as IdTokenClaims).extension_portalcrm_userType.split(',');

    useEffect(() => {
        let tabList = [UpdateProfileStepsEnum.PersonalDetails];
        if (alumniFilter(userRoles) || famFilter(userRoles) || affiliateAssociateFilter(userRoles)) {
            tabList.push(UpdateProfileStepsEnum.PostalAddress);
            tabList.push(UpdateProfileStepsEnum.LocationAndPreferences);
        }

        if (pcsFilter(userRoles)) {
            tabList.push(UpdateProfileStepsEnum.PcsDetails);
        }

        if (pcsFilter(userRoles) || alumniFilter(userRoles) || famFilter(userRoles)) {
            tabList.push(UpdateProfileStepsEnum.WorkDetails);
        }
        setTabs(tabList);
    }, []);

    const [arePageValuesChanged, setArePageValuesChanged] = useState(false);
    const [showDialog, setShowDialog] = useState(false);

    useEffect(() => {
        if (accounts.length > 0 && accounts[0]?.idTokenClaims) {
            setPersonId(accounts[0].idTokenClaims[UserClaims.EXTENSION_PORTALCRM_PERSONID] as string);
        }
    }, [accounts]);

    const [personId, setPersonId] = useState('');

    const dispatch = useAppDispatch();

    const { personalDetails, updatePersonalDetailsStatus, updateVerifyPersonalDetailsStatus }
        = useAppSelector(state => state.personalDetail);
    const { alumnusDetails } = useAppSelector(state => state.alumniDetails);
    const { famDetails, updateFamDetailsStatus } = useAppSelector(state => state.famDetails);
    const { saveWorkpostStatus } = useAppSelector(state => state.workpost);

    const { staticDataStateStatus } = useReferenceData();
    const { personalDetailsStatus } = useGetPersonalDetails();
    const { famDetailsStatus } = useGetFamDetails();
    const { alumnusDetailsStatus } = useGetAlumniDetails();

    function getStepContent(step: string) {
        switch (step) {
            case UpdateProfileStepsEnum.PersonalDetails:
                return <PersonalDetails />
            case UpdateProfileStepsEnum.PostalAddress:
                return <MemberPostalAddress />
            case UpdateProfileStepsEnum.LocationAndPreferences:
                return <LocationAndPreferences />
            case UpdateProfileStepsEnum.PcsDetails:
                return <PCSDetails />
            case UpdateProfileStepsEnum.WorkDetails:
                return <WorkDetails handleSave={handleSave} spaName={SpaName.UPDATE_PROFILE} />
            default:
                throw new Error('Unknown step');
        }
    }

    const [activeStep, setActiveStep] = useState(0);
    const [expectedStep, setExpectedStep] = useState(0);
    const [isPersonalDetailsSubmitted, setIsPersonalDetailsSubmitted] = useState(false);
    const currentValidationSchema = updateProfileValidationSchemas.find(x => x.index === tabs[activeStep])?.schema ?? yup.object();

    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(currentValidationSchema as any),
        defaultValues: {
            FirstName: '',
            Surname: '',
            MiddleName: '',
            PreferredName: '',
            PreferredEmail1: '',
            AlternateEmail2: '',
            AlternateEmail3: '',
            DateOfBirth: null,
            Title: '',
            Gender: '',
            CountryOfNationality: '',
            PreferredPhoneNumber: '',
            AlternatePhoneNumber: '',
            Discipline: '',
            AreaOfPracticeMoreDetails: '',
            LinkedInUrl: '',
            PostalAddressLines: '',
            PostalAddressCountry: '',
            LocationCountry: '',
            CountrySubdivision: '',
            SurgeonsScopeMagazine: '',
            TheSurgeonJournal: '',
            AlumniMagazinePreference: '',
            OptIntoDirectMarketing: 'No',
            ShareMyEmailWithMyClassmates: 'No',
            IMCNumber: '',
            RegistrationType: '',
            Scheme: '',
            Specialty: '',
            EngagedInPracticeOfMedicine: '0',
            SubSpecialty: '',
            UserConfirmation: '',
            StartDate: '',
            PostLevel: '',
            Country: '',
            NameOfEmployer: '',
            OtherNameOfEmployer: '',
            NameOfClinicalSupervisor: '',
            JobTitle: ''
        }
    });
    const { trigger, watch } = methods;
    const form = methods.watch();

    useEffect(() => {
        watch((_, { name, type }) => {
            if (type === 'change') {
                setArePageValuesChanged(true);
            }
        });
    }, [watch]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        if (arePageValuesChanged === true) {
            setShowDialog(true);
            setExpectedStep(newValue);
        }
        else {
            setActiveStep(newValue);
        }

        if (isPersonalDetailsSubmitted === false) {
            switch (tabs[activeStep]) {
                case UpdateProfileStepsEnum.PersonalDetails:
                    dispatch(updateVerifyPersonalDetailsAsync()).then((response: any) => {
                        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                            toast.success(MESSAGE_DETAILS_SAVED);
                        }
                    });
                    break;
            }
        }
    };

    const handleClose = () => {
        setShowDialog(false);
    };

    const handleCloseAndContinue = () => {
        setShowDialog(false);
        setArePageValuesChanged(false);
        setActiveStep(expectedStep);
    };

    const MapToPersonalDetails = (values: any) => {
        const preferredEmail = values?.PreferredEmail1 === "" ? null : values?.PreferredEmail1;
        const email2 = values?.AlternateEmail2 === "" ? null : values?.AlternateEmail2;
        const email3 = values?.AlternateEmail3 === "" ? null : values?.AlternateEmail3;

        let isEmailUpdated = personalDetails?.preferredEmail === preferredEmail
            && personalDetails?.email2 === email2 && personalDetails?.email3 === email3;

        const personalDetailsObj: PersonalDetailsDto = {
            id: personId,
            firstName: values?.FirstName,
            lastName: values?.Surname,
            middleName: values?.MiddleName,
            prefferedName: values?.PreferredName,
            preferredEmail: values?.PreferredEmail1,
            email2: values?.AlternateEmail2,
            email3: values?.AlternateEmail3,
            dateOfBirth: formatDate(values?.DateOfBirth),
            titleId: values?.Title,
            genderId: values?.Gender,
            nationalityId: values?.CountryOfNationality,
            mobile: values?.PreferredPhoneNumber,
            alternatePhoneNumber: values?.AlternatePhoneNumber,
            disciplineId: values?.Discipline,
            areaOfPracticeId: values?.AreaOfPracticeMoreDetails,
            linkedin: values?.LinkedInUrl,
            emailchanged: !isEmailUpdated
        }

        return personalDetailsObj;
    }

    const MapToPostalAddress = (values: any) => {
        let personalDetailsObj = {
            ...personalDetails,
            postalAddress: values?.PostalAddressLines,
            postalAddressCountryId: values?.PostalAddressCountry,
            dateOfBirth: (personalDetails?.dateOfBirth && formatDate(new Date(personalDetails?.dateOfBirth)).toLocaleString()) ?? ''
        };

        return personalDetailsObj;
    }

    const MapToLocationAndPreferences = () => {
        const data: PersonalDetailsDto = {
            ...personalDetails, id: personId,
            locationCountryId: form.LocationCountry as string,
            locationCountrySubdivisionId: form.CountrySubdivision === MESSAGE_NOT_A_VALUE ? '' : form.CountrySubdivision as string,
            dateOfBirth: (personalDetails?.dateOfBirth && formatDate(new Date(personalDetails?.dateOfBirth)).toLocaleString()) ?? ''
        }
        return data;
    }

    const MapToFamDetails = (values: any) => {
        let famDetailsObj = {
            ...famDetails,
            scopePreferenceId: values?.SurgeonsScopeMagazine,
            surgeonJournalPreferenceId: values?.TheSurgeonJournal,
            personId: personId
        };

        return famDetailsObj;
    }

    const MapToAlumniDetails = (values: any) => {
        let alumniDetailsObj = {
            ...alumnusDetails,
            alumniMagPreferenceId: values?.AlumniMagazinePreference,
            directMarketingPreference: values?.OptIntoDirectMarketing === "Yes" ? true : false,
            shareEmailPreference: values?.ShareMyEmailWithMyClassmates === "Yes" ? true : false,
            personId: personId
        };

        return alumniDetailsObj;
    }

    function MapToRegistrationDetailsDto() {
        const data: PCSDetailsDto = {
            imcNumber: parseInt(form.IMCNumber as string),
            registrationTypeId: form.RegistrationType as string,
            schemeId: form.Scheme as string,
            specialtyId: form.Specialty as string,
            engagedInPracticeOfMedicine: form.EngagedInPracticeOfMedicine === '1',
            subSpeciality: form.SubSpecialty as string,
            declaration: (form.UserConfirmation as string).length > 0
        }
        return data;
    }

    function MapToWorkDetailsDto() {
        const data: WorkPostDto = {
            id: null,
            startDate: formatDate(new Date(form.StartDate)),
            workPostLevelId: form.PostLevel as string,
            countryId: form.Country as string,
            employerId: form.NameOfEmployer as string,
            employerOther: form.NameOfEmployer === '' ? form.OtherNameOfEmployer as string : '',
            clinicalSupervisor: form.NameOfClinicalSupervisor as string,
            jobTitle: form.JobTitle as string
        }
        return data;
    }

    const handleSave = async () => {
        const isStepValid = await trigger();
        if (!isStepValid) {
            trackPortalEvent(portalScreenContext(PortalTelemetryScreen.UpdateProfile),
                PortalTelemetryEvent.ValidationBlocked, {
                step: ({
                    [UpdateProfileStepsEnum.PersonalDetails]: 'personal_details',
                    [UpdateProfileStepsEnum.PostalAddress]: 'postal_address',
                    [UpdateProfileStepsEnum.LocationAndPreferences]: 'location_and_preferences',
                    [UpdateProfileStepsEnum.PcsDetails]: 'pcs_details',
                    [UpdateProfileStepsEnum.WorkDetails]: 'work_details'
                } as Record<string, string>)[tabs[activeStep]],
                validationErrors: validationIssues(PortalTelemetryScreen.UpdateProfile, methods.getFieldState)
            });
        }

        if (isStepValid) {
            appInsights.trackEvent({
                name: 'UpdateProfileSave', properties: {
                    component: 'UpdateProfile', tab: tabs[activeStep]
                }
            });
            switch (tabs[activeStep]) {
                case UpdateProfileStepsEnum.PersonalDetails:
                    if (form) {
                        dispatch(updatePersonalDetailsStateAsync(MapToPersonalDetails(form)))
                            .then((response) => {
                                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                                    dispatch(getPersonalDetailsAsync(personId)).then((personalDetailsResponse) => {
                                        if (personalDetailsResponse.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                                            setIsPersonalDetailsSubmitted(true);
                                            setArePageValuesChanged(false);
                                            toast.success(MESSAGE_DETAILS_SAVED);
                                        }
                                    });
                                }
                            });
                    }
                    break;
                case UpdateProfileStepsEnum.PostalAddress:
                    if (form) {
                        dispatch(updatePersonalDetailsStateAsync(MapToPostalAddress(form)))
                            .then((response) => {
                                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                                    setArePageValuesChanged(false);
                                    toast.success(MESSAGE_DETAILS_SAVED);
                                    dispatch(getPersonalDetailsAsync(personId));
                                }
                            });
                    }
                    break;
                case UpdateProfileStepsEnum.LocationAndPreferences:
                    if (form) {
                        if (alumniFilter(userRoles) && (famFilter(userRoles) || affiliateAssociateFilter(userRoles))) {
                            const pageLoadPromise = async () => await Promise.all([
                                dispatch(updatePersonalDetailsStateAsync(MapToLocationAndPreferences())),
                                dispatch(updateAlumniDetails(MapToAlumniDetails(form))),
                                dispatch(updateFAMDetails(MapToFamDetails(form)))
                            ]);

                            pageLoadPromise().finally(() => {
                                const famAlumniPromise = async () => await Promise.all([
                                    dispatch(getAlumniDetails(personId)),
                                    dispatch(getFAMDetails(personId)),
                                    dispatch(getPersonalDetailsAsync(personId))
                                ]);

                                famAlumniPromise().finally(() => {
                                    setArePageValuesChanged(false);
                                    toast.success(MESSAGE_DETAILS_SAVED);
                                });
                            });
                        }
                        else if (alumniFilter(userRoles)) {
                            const pageLoadPromise = async () => await Promise.all([
                                dispatch(updatePersonalDetailsStateAsync(MapToLocationAndPreferences())),
                                dispatch(updateAlumniDetails(MapToAlumniDetails(form)))
                            ]);

                            pageLoadPromise().finally(() => {
                                const alumniPromise = async () => await Promise.all([
                                    dispatch(getAlumniDetails(personId)),
                                    dispatch(getPersonalDetailsAsync(personId))
                                ]);

                                alumniPromise().finally(() => {
                                    setArePageValuesChanged(false);
                                    toast.success(MESSAGE_DETAILS_SAVED);
                                });
                            });
                        }
                        else if (famFilter(userRoles) || affiliateAssociateFilter(userRoles)) {
                            const pageLoadPromise = async () => await Promise.all([
                                dispatch(updatePersonalDetailsStateAsync(MapToLocationAndPreferences())),
                                dispatch(updateFAMDetails(MapToFamDetails(form)))
                            ]);

                            pageLoadPromise().finally(() => {
                                const famPromise = async () => await Promise.all([
                                    dispatch(getFAMDetails(personId)),
                                    dispatch(getPersonalDetailsAsync(personId))
                                ]);

                                famPromise().finally(() => {
                                    setArePageValuesChanged(false);
                                    toast.success(MESSAGE_DETAILS_SAVED);
                                });
                            });
                        }
                    }
                    break;
                case UpdateProfileStepsEnum.PcsDetails:
                    if (form) {
                        let registrationDetails = MapToRegistrationDetailsDto();
                        dispatch(saveRegistrationDetailsAsync(registrationDetails)).then((response: any) => {
                            if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                                toast.success(MESSAGE_DETAILS_SAVED);
                                setArePageValuesChanged(false);
                                dispatch(getRegistrationDetailsAsync());
                            }
                        });
                    }
                    break;
                case UpdateProfileStepsEnum.WorkDetails:
                    if (form) {
                        let workpostDto = MapToWorkDetailsDto();
                        dispatch(saveWorkpostAsync(workpostDto)).then((response: any) => {
                            if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                                toast.success(MESSAGE_DETAILS_SAVED);
                                setArePageValuesChanged(false);
                                dispatch(getWorkpostListAsync());
                            }
                        });
                    }
                    break;
                default:
                    return "not a valid step";
            }
            handleClose();
        }
    };

    usePortalScreen(portalScreenContext(PortalTelemetryScreen.UpdateProfile), ({ [UpdateProfileStepsEnum.PersonalDetails]: 'personal_details', [UpdateProfileStepsEnum.PostalAddress]: 'postal_address', [UpdateProfileStepsEnum.LocationAndPreferences]: 'location_and_preferences', [UpdateProfileStepsEnum.PcsDetails]: 'pcs_details', [UpdateProfileStepsEnum.WorkDetails]: 'work_details' } as Record<string, string>)[tabs[activeStep]], !((staticDataStateStatus === ApiRequestStatus.Pending || updatePersonalDetailsStatus === ApiRequestStatus.Pending
        || personalDetailsStatus === ApiRequestStatus.Pending || famDetailsStatus === ApiRequestStatus.Pending
        || alumnusDetailsStatus === ApiRequestStatus.Pending || updateFamDetailsStatus === ApiRequestStatus.Pending
        || saveWorkpostStatus === ApiRequestStatus.Pending || updateVerifyPersonalDetailsStatus === ApiRequestStatus.Pending)));

    if (staticDataStateStatus === ApiRequestStatus.Pending || updatePersonalDetailsStatus === ApiRequestStatus.Pending
        || personalDetailsStatus === ApiRequestStatus.Pending || famDetailsStatus === ApiRequestStatus.Pending
        || alumnusDetailsStatus === ApiRequestStatus.Pending || updateFamDetailsStatus === ApiRequestStatus.Pending
        || saveWorkpostStatus === ApiRequestStatus.Pending || updateVerifyPersonalDetailsStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <>
        <FormProvider {...methods}>
            <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
                <Typography component="h1" variant="h4">
                    Update profile
                </Typography>

                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }} sx={{ margin: '2%' }}>
                    <Typography variant='subtitle1'>
                        Please review your details below. If they are incorrect, please update and click 'Save'.
                        If the information is correct, please verify them by clicking 'Save'.
                    </Typography>
                </Grid>

                <Tabs value={activeStep}
                    onChange={portalAction(portalScreenContext(PortalTelemetryScreen.UpdateProfile), `${PortalTelemetryScreen.UpdateProfile}.onChange.handleChange`, handleChange)}
                    textColor="primary"
                    indicatorColor="primary"
                    aria-label="primary tabs example"
                    sx={{
                        '& .MuiTabs-flexContainer': {
                            flexWrap: 'wrap',
                        },
                    }}>
                    {tabs.map((label: string, inedex: number) => (
                        <Tab key={label} value={inedex} label={label} />
                    ))}
                </Tabs>
                {getStepContent(tabs[activeStep])}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Grid container rowSpacing={4}>
                        <Grid size={{ xs: 12 }}></Grid>
                        {tabs[activeStep] !== UpdateProfileStepsEnum.WorkDetails &&
                            <>
                                <Grid size={{ xs: 8 }}></Grid>
                                <Grid size={{ xs: 3 }} sx={{ display: 'flex', justifyContent: 'end' }}>
                                    <Button variant="contained" color="primary" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.UpdateProfile), `${PortalTelemetryScreen.UpdateProfile}.onClick.handleSave`, handleSave)}>
                                        Save
                                    </Button>
                                </Grid>
                                <Grid size={{ xs: 1 }}></Grid>
                            </>}
                    </Grid>
                </Box>
            </Paper>

            <Dialog
                open={showDialog}
                onClose={handleClose}
                aria-labelledby="unsaved-changes-dialog"
                aria-describedby="unsaved-changes-dialog"
            >
                <DialogTitle id="unsaved-changes-dialog">
                    {"Unsaved changes?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="unsaved-changes-dialog">
                        This page contains unsaved changes. Do you still wish to leave this page?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button variant="contained" color="primary"
                        onClick={portalAction(portalScreenContext(PortalTelemetryScreen.UpdateProfile), `${PortalTelemetryScreen.UpdateProfile}.onClick.handleCloseAndContinue`, handleCloseAndContinue)} autoFocus>
                        Yes
                    </Button>
                    <Button onClick={portalAction(portalScreenContext(PortalTelemetryScreen.UpdateProfile), `${PortalTelemetryScreen.UpdateProfile}.onClick.handleClose`, handleClose)}>No</Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    </>
}
